import React, { useState } from "react";
import { makeStyles } from "@alpha/ui-lib/theme";
import { Box, Link, Card as MuiCard } from '@alpha/ui-lib/ui/external';
import { Typography } from "@alpha/ui-lib/ui/Typography";
import {faArrowUpRightFromSquare, faXmark} from '@fortawesome/pro-light-svg-icons';
import {faPlay} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useTranslation } from "react-i18next";

const Card = MuiCard as React.FC<React.ComponentProps<typeof MuiCard>>;

export const useStyles = makeStyles(
  () => ({
    crossBorderWeb: {
      gridColumn: 'span 2',
      padding: '16px',
      overflow: 'hidden',
      minWidth: 0,
      borderRadius: '10px',
    },
    crossBorderWebDescription: {
      fontWeight: 400,
      fontSize: '13px',
      marginTop: '8px',
      color: '#000000BF',
    },
    videoGuides: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      '@media (max-width: 800px)': {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
      gridGap: '8px',
      width: '100%',
    },
    videoGuide: {
      minWidth: 0,
      border: '1px solid #EAEAEA',
      borderRadius: '5px',
      overflow: 'hidden',
      padding: '8px',
      marginTop: '6px'
    },
    video: {
      backgroundColor: '#3D1629',
      marginBottom: '8px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '92px',
      width: '100%',
      borderRadius: '3px',
    },
    videoOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1300,
    },
    videoPopup: {
      position: 'relative',
      width: '776px',
      height: '472px',
      backgroundColor: '#000',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    videoPopupClose: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      cursor: 'pointer',
      color: '#FFFFFF',
      zIndex: 1,
    },
    viewAllVideoGuides: {
      marginTop: '16px',
      float: 'right',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  }),
  { name: 'CrossBorderWeb' },
);

type VideoGuide = {
  title: string;
  description?: string;
  videoUrl: string;
}

const CrossBorderWeb: React.FC = () => {
  const [expandVideo, setExpandVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const { t } = useTranslation();
  const classes = useStyles();

  const handleOpenVideo = (videoUrl: string) => {
    setExpandVideo(true);
    setVideoUrl(videoUrl);
  }

  const videoGuides: VideoGuide[] = [
    {
      title: 'Creating a beneficiary',
      videoUrl: 'https://player.vimeo.com/video/652641460?h=aaaca16856',
    },
    {
      title: 'Editing a beneficiary',
      videoUrl: 'https://player.vimeo.com/video/652638385?h=d370076e0b',
    },
    {
      title: 'Making a payment',
      videoUrl: 'https://player.vimeo.com/video/644951042?h=97dd9c487a',
    },
    {
      title: 'Booking the deal',
      videoUrl: 'https://player.vimeo.com/video/652627034?h=b39cb1ef4d',
    },
    {
      title: 'Using a spot trade',
      videoUrl: 'https://player.vimeo.com/video/644941206?h=321b095555',
    },
    {
      title: 'Finishing a spot trade',
      videoUrl: 'https://player.vimeo.com/video/644938261?h=80785260d7',
    },
    {
      title: 'Drawing down against a forward',
      videoUrl: 'https://player.vimeo.com/video/644919362?h=dfc602cf7f',
    }
  ];
  
  const renderVideoGuides = () => {
    return (
      <>
      <Box className={classes.videoGuides}>
        {videoGuides.map((guide) => (
          <Box className={classes.videoGuide}>
            <Box
              className={classes.video}
              onClick={() => handleOpenVideo(guide.videoUrl)}
            >
              <FontAwesomeIcon icon={faPlay as IconProp} color="#FFFFFF80" style={{ width: '24px', height: '24px' }} />
            </Box>
            <strong>{guide.title}</strong>
            </Box>
          ))}
        </Box>
      </>
    );
  }

  return (
    <>
    <Card className={classes.crossBorderWeb}>
        <Typography variant="h3" style={{ fontWeight: 600 }}>{t('cross_border_web')}</Typography>
        <div className={classes.crossBorderWebDescription}>{t('watch_step_by_step_video_guides_to_help_you_get_started_with_the_corpay_platform')}</div>
        <div style={{ marginTop: '24px' }}>
        <Typography variant="h4" style={{ fontWeight: 600, fontSize: '13px' }}>{t('video_guides')}:</Typography>
          {renderVideoGuides()}
          <a href="https://help.crossborder.corpay.com/en-ca/Content/Topics/Video%20Tutorials.htm" target="_blank" rel="noopener noreferrer" className={classes.viewAllVideoGuides}>{t('view_all_video_guides')} <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginLeft: '8px' }} /></a>
        </div>
    </Card>
    {expandVideo && (
      <Box className={classes.videoOverlay} onClick={() => setExpandVideo(false)}>
        <Box className={classes.videoPopup} onClick={(e) => e.stopPropagation()}>
          <Box className={classes.videoPopupClose} onClick={() => setExpandVideo(false)}>
            <FontAwesomeIcon icon={faXmark as IconProp} size="lg" />
          </Box>
          <div style={{ padding: '56.32% 0 0 0', position: 'relative' }}>
            <iframe
              src={videoUrl}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          </div>
        </Box>
      </Box>
    )}
    </>
  )
};

export default CrossBorderWeb;